import { parseAsString, useQueryState } from 'nuqs';
import type { PendingPaymentDto } from '@barbeiropro/contracts';
import { DataTable, type DataColumn } from '@/components/shared/data-table';
import { useTableParams } from '@/components/shared/use-table-params';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { fmtDateTime, money } from '@/lib/format';
import { useBarbers, useTimezone } from '@/lib/org';
import { usePendingPayments } from '../api';

export function PendingTable({ branchId, onReceive }: { branchId: string; onReceive: (appointmentId: string) => void }) {
  const tz = useTimezone();
  const params = useTableParams({ sort: 'completedAt', order: 'asc' });
  const [barberId, setBarberId] = useQueryState('barbeiro', parseAsString.withOptions({ history: 'replace' }));
  const barbers = useBarbers();
  const q = usePendingPayments({ ...params.query, branchId, barberId });

  const columns: DataColumn<PendingPaymentDto>[] = [
    { header: 'Concluído em', accessorFn: (r) => r.completedAt, cell: ({ row }) => <span className="tabular">{row.original.completedAt ? fmtDateTime(row.original.completedAt, tz) : '-'}</span>, meta: { sortKey: 'completedAt' } },
    { header: 'Cliente', accessorKey: 'customerName' },
    { header: 'Serviço', accessorKey: 'serviceName' },
    { header: 'Profissional', accessorKey: 'barberName' },
    { header: 'Valor', accessorFn: (r) => r.priceCents, cell: ({ row }) => money(row.original.priceCents), meta: { sortKey: 'priceCents', align: 'right' } },
    {
      id: 'actions',
      header: () => <span className="sr-only">Ações</span>,
      cell: ({ row }) => (
        <Button size="sm" onClick={() => onReceive(row.original.appointmentId)}>
          Receber
        </Button>
      ),
      meta: { align: 'right' },
    },
  ];

  return (
    <DataTable
      columns={columns}
      data={q.data}
      params={params}
      isLoading={q.isLoading}
      error={q.error}
      onRetry={() => q.refetch()}
      searchPlaceholder="Buscar por cliente ou serviço"
      empty={{ title: 'Nenhum atendimento aguardando recebimento', description: 'Atendimentos concluídos e ainda não pagos aparecem aqui.' }}
      filters={
        <Select value={barberId ?? 'all'} onValueChange={(v) => { void setBarberId(v === 'all' ? null : v); void params.set({ page: 1 }); }}>
          <SelectTrigger aria-label="Profissional" className="w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os profissionais</SelectItem>
            {(barbers.data ?? []).filter((b) => b.branchId === branchId).map((b) => (
              <SelectItem key={b.id} value={b.id}>
                {b.displayName}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      }
    />
  );
}
